import React from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent } from 'react-native';

import OddsDown from '@/assets/icons/odds-down';
import OddsUp from '@/assets/icons/odds-up';
import { LineSeparator } from '@/components/LineSeparator';
import { Odds } from '@/components/Odds';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import {
    useActiveEventCount,
    useActiveSelectionCount,
    useComboOdds,
    usePreviousComboOdds,
} from '@/feature/betslip-sbk/hooks/betslip-hooks';

import { ROW_HEIGHT } from './OddsChangeItemSingle';

type OddsChangeItemParlayProps = {
    columnWidths: { oldOdds: number; newOdds: number };
    createLayoutHandler: (column: 'oldOdds' | 'newOdds') => (event: LayoutChangeEvent) => void;
};

export const OddsChangeItemParlay = ({ columnWidths, createLayoutHandler }: OddsChangeItemParlayProps) => {
    const { t } = useTranslation('betslip_sbk');

    const comboOdds = useComboOdds();
    const previousComboOdds = usePreviousComboOdds();
    const activeSelectionCount = useActiveSelectionCount();
    const activeEventCount = useActiveEventCount();

    const oddsIncreased = comboOdds > previousComboOdds;

    return (
        <Box height={ROW_HEIGHT}>
            <Row py="s16" alignItems="center" justifyContent="space-between">
                <Box flex={1} pr="s16" gap="s4">
                    <Text variant="bodySmall" color="neutral300" numberOfLines={1}>
                        {t('events_count', { count: activeEventCount })}
                    </Text>
                    <Text variant="titleMedium" color={ds.colorToken.text.primary} numberOfLines={1}>
                        {t('parlay_pick', { count: activeSelectionCount })}
                    </Text>
                </Box>
                <Box minWidth={columnWidths.oldOdds} pr="s16">
                    <Odds
                        odds={previousComboOdds}
                        variant="bodyMedium"
                        color={ds.colorToken.text.primary}
                        textAlign="right"
                        onLayout={createLayoutHandler('oldOdds')}
                    />
                </Box>
                <Row minWidth={columnWidths.newOdds} alignItems="center" justifyContent="flex-end" gap="s4">
                    {oddsIncreased ? <OddsUp /> : <OddsDown />}
                    <Odds
                        odds={comboOdds}
                        variant="bodyMedium"
                        color={oddsIncreased ? ds.colorToken.text.success : ds.colorToken.text.danger}
                        onLayout={createLayoutHandler('newOdds')}
                    />
                </Row>
            </Row>
            <LineSeparator />
        </Box>
    );
};
